import { NgClass } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Tab, TabsComponent } from 'components/tabs/tabs.component';
import { TableComponent } from 'components/table/table.component';
import { Subject, Subscription, interval, takeUntil } from 'rxjs';
import { API_VISIT_LIST, ApiService } from 'services/api.service';
import { AuthorizationService } from 'services/authorization.service';
import { NotificationService } from 'services/notification.service';
import { ClaimDirective } from 'src/app/directive/claim.directive';
import { ReadingRoomMinimalListItem } from 'types/reading-room.interface';
import { FilterType, TableColumn } from 'types/table-column.interface';
import { AttendanceStatus, CLAIM_SCOPES, CLAIM_TYPES } from 'types/user.interface';
import { BackofficeStatus } from 'types/visit-room.interface';
import { checkClaims, getPolicyStructuralUnitIds } from 'util/user.utility';

export const KEY_LAST_ACTIVE_SECTION = 'lastActiveSection';

interface Section {
  key: string;
  hasClaims: boolean;
}

@Component({
  selector: 'app-workspace-page',
  standalone: true,
  imports: [TranslateModule, NgClass, TableComponent, TabsComponent, ClaimDirective],
  templateUrl: './workspace-page.component.html',
  styleUrl: './workspace-page.component.scss'
})
export class WorkspacePageComponent implements OnInit {
  public readonly CLAIM_TYPES = CLAIM_TYPES;
  public readonly POLICY_TYPE_SERVICE = CLAIM_SCOPES['serviceSubmission'];
  public readonly POLICY_TYPE_MY_WORKSPACE = CLAIM_SCOPES['myWorkspace'];
  @ViewChild('myAppsTable') myAppsTable!: TableComponent;
  @ViewChild('myReadingRoomAppsTable') myReadingRoomAppsTable!: TableComponent;
  @ViewChild('allAppsTable') allAppsTable!: TableComponent;

  private destroy$ = new Subject<void>();
  private autoRefreshSub?: Subscription;
  public readonly checkClaims = checkClaims;
  userId: string = '';
  codesList: string[] = [];
  public readonly apiPath = API_VISIT_LIST;
  apiPathMyApps = '';
  structuralUnitIds: string[] = [];
  hasGlobalReadingRoomScope: boolean = false;
  sections: Section[] = [
    {
      key: 'myApps',
      hasClaims: checkClaims(this.POLICY_TYPE_MY_WORKSPACE, this.CLAIM_TYPES['workspaceMyServiceSubmissions'])
    },
    {
      key: 'myReadingRoomApps',
      hasClaims: checkClaims(this.POLICY_TYPE_MY_WORKSPACE, this.CLAIM_TYPES['workspaceStructuralUnitServiceSubmissions'])
    },
    {
      key: 'allApps',
      hasClaims: checkClaims(this.POLICY_TYPE_MY_WORKSPACE, this.CLAIM_TYPES['workspaceAllServiceSubmissions'])
    }
  ];

  appTableColumns: TableColumn[] = [
    {
      name: 'pages.workspace.tableColumns.client',
      key: 'client.fullName',
      filter: FilterType.Search,
      sortEnabled: true,
      sortKey: 'ClientFullName'
    },
    {
      name: 'pages.workspace.tableColumns.identityShort',
      key: 'client.officialDocumentId',
      sortKey: 'officialDocumentId',
      sortEnabled: true,
      filter: FilterType.Search
    },
    {
      name: 'pages.workspace.tableColumns.country',
      key: 'client.country.name',
      filter: FilterType.Search,
      sortEnabled: true,
      sortKey: 'ClientCountryName'
    },
    {
      name: 'pages.workspace.tableColumns.phone',
      key: 'client.phone',
      filter: FilterType.Search,
      sortEnabled: true,
      sortKey: 'ClientPhone'
    },
    {
      name: 'pages.workspace.tableColumns.email',
      key: 'client.email',
      filter: FilterType.Search,
      sortEnabled: true,
      sortKey: 'ClientEmail'
    },
    {
      name: 'pages.workspace.tableColumns.applicationNumber',
      key: 'code',
      filter: FilterType.Search,
      sortEnabled: true,
      sortKey: 'submittedOn-code'
    },
    {
      name: 'pages.workspace.tableColumns.readingRoomCode',
      key: 'readingRoom.code',
      multiselectFilterName: 'readingRoomCodes',
      filter: FilterType.MultiSelect,
      filterOptions: this.codesList,
      sortEnabled: true,
      sortKey: 'ReadingRoomCode'
    },
    {
      name: 'pages.workspace.tableColumns.submissionDateTime',
      key: 'submittedOn',
      filter: FilterType.DateTime,
      type: 'date',
      sortEnabled: true,
      sortKey: 'submittedOn'
    },
    {
      name: 'pages.workspace.tableColumns.visitDate',
      key: 'visitDate',
      sortEnabled: true,
      filter: FilterType.Date,
      type: 'date',
      sortKey: 'visitDate'
    },
    {
      name: 'pages.workspace.tableColumns.visitTimeFrom',
      key: 'visitTimeFrom',
      sortEnabled: true,
      filter: FilterType.Time,
      sortKey: 'VisitTimeFrom'
    },
    {
      name: 'pages.workspace.tableColumns.backofficeStatus',
      key: 'backofficeStatus',
      multiselectFilterName: 'backofficeStatuses',
      styleClass: 'status-column',
      sortEnabled: true,
      filter: FilterType.MultiSelect,
      valueTranslate: 'pages.service.visitRoom.backofficeStatus.',
      filterOptions: Object.values(BackofficeStatus),
      sortKey: 'backofficeStatus'
    },
    {
      name: 'pages.workspace.tableColumns.attendanceStatus',
      key: 'attendanceStatus',
      multiselectFilterName: 'attendanceStatuses',
      sortEnabled: true,
      filter: FilterType.MultiSelect,
      valueTranslate: 'pages.service.visitRoom.attendanceStatus.',
      filterOptions: Object.values(AttendanceStatus),
      sortKey: 'attendanceStatus'
    },
    {
      name: 'pages.workspace.tableColumns.responsibleUser',
      key: 'responsibleUser.fullName',
      sortEnabled: true,
      filter: FilterType.Search,
      sortKey: 'ResponsibleUserFullName'
    },
    {
      name: 'pages.workspace.tableColumns.editDate',
      key: 'changedByClientOn',
      filter: FilterType.DateTime,
      type: 'date',
      sortEnabled: true,
      sortKey: 'changedByClientOnDate'
    },
    {
      name: 'pages.workspace.tableColumns.editTime',
      key: 'changedByClientOnTimeOnly',
      filter: FilterType.TimeUtc,
      sortEnabled: true,
      sortKey: 'changedByClientOnTimeOnly',
      type: 'time'
    }
  ];

  get myAppsTableColumns(): TableColumn[] {
    const excludedKeys = ['responsibleUser.fullName', 'attendanceStatus'];
    return this.appTableColumns.filter((col) => !excludedKeys.includes(col.key));
  }

  get myReadingRoomAppsAppsTableColumns(): TableColumn[] {
    return this.appTableColumns.filter((col) => col.key !== 'attendanceStatus');
  }

  activeSection: string = sessionStorage.getItem(KEY_LAST_ACTIVE_SECTION) || 'myApps';
  isExcelDownloading: boolean = false;

  get workspaceTabs(): Tab[] {
    return this.sections
      .filter((section) => section.hasClaims)
      .map((section) => ({
        key: section.key,
        label: 'pages.workspace.' + section.key,
        suffix: undefined
      }));
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    public apiService: ApiService,
    private router: Router,
    private authService: AuthorizationService,
    private notificationService: NotificationService
  ) {}

  async ngOnInit() {
    this.userId = this.authService.getUserId();

    this.apiService.getReadingRoomMinimalList().subscribe(
      (data: ReadingRoomMinimalListItem[]) => {
        data.forEach((room: ReadingRoomMinimalListItem) => {
          this.codesList.push(room.code);
        });
      },
      (error: any) => {
        console.error(error);
      }
    );

    // Reading-room "manā str.v." must only see archives where the user holds the reading-room
    // (ServiceSubmission) role — not the pooled structuralUnits, which merges every role's units.
    const { structuralUnitIds, isGlobalScope } = getPolicyStructuralUnitIds(this.authService.userSecurityInfo(), this.POLICY_TYPE_SERVICE);
    this.structuralUnitIds = structuralUnitIds;
    this.hasGlobalReadingRoomScope = isGlobalScope;

    this.notificationService.startPolling();
    this.apiPathMyApps = `${API_VISIT_LIST}`;
    this.activatedRoute.queryParams.subscribe((params) => {
      const section = params['activeSection'];
      if (section && this.sections.some((s) => s.key === section)) {
        this.activeSection = section;
      }
    });

    // Land on a tab the user can actually see — e.g. a system administrator has only the
    // All-scope claims, so the 'myApps' default would show content behind a hidden tab (LNA-1979).
    if (!this.sections.some((s) => s.key === this.activeSection && s.hasClaims)) {
      this.activeSection = this.sections.find((s) => s.hasClaims)?.key ?? this.activeSection;
    }

    this.startAutoRefresh();

    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(() => this.refreshVisibleTable());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.autoRefreshSub?.unsubscribe();
  }

  private startAutoRefresh(): void {
    const FIVE_MIN = 5 * 60 * 1000;
    this.refreshVisibleTable();
    this.autoRefreshSub = interval(FIVE_MIN)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshVisibleTable());
  }

  private refreshVisibleTable(): void {
    switch (this.activeSection) {
      case 'myApps':
        this.myAppsTable?.getTableData?.();
        break;
      case 'myReadingRoomApps':
        this.myReadingRoomAppsTable?.getTableData?.();
        break;
      case 'allApps':
        this.allAppsTable?.getTableData?.();
        break;
    }
  }

  setActiveSection(section: string): void {
    this.activeSection = section;
    sessionStorage.setItem(KEY_LAST_ACTIVE_SECTION, section);
    this.refreshVisibleTable();

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {},
      replaceUrl: true
    });
  }

  getExcelFile(): void {
    if (!this.isExcelDownloading) {
      this.isExcelDownloading = true;
      this.apiService
        .getReadingRoomSubmissionExcel(
          this.activeSection === 'myApps',
          this.activeSection === 'myReadingRoomApps' && !this.hasGlobalReadingRoomScope ? this.structuralUnitIds : null
        )
        .subscribe(
          (data: any) => {
            if (data) {
              this.saveExcelFile(data);
              this.isExcelDownloading = false;
            }
          },
          (error: any) => {
            console.error(error);
          }
        );
    }
  }

  saveExcelFile(blob: Blob, fileName: string = 'pieteikumi.xlsx'): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    switch (this.activeSection) {
      case 'myApps':
        a.download = 'Mani_Aktualie_Pieteikumi.xlsx';
        break;
      case 'myReadingRoomApps':
        a.download = 'Pieteikumi_Manas_Lasitavas.xlsx';
        break;
      case 'allApps':
        a.download = 'Visi_Pieteikumi.xlsx';
        break;
      default:
        a.download = fileName;
        break;
    }

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  createVisit(): void {
    this.router.navigate(['/jauns-pakalpojuma-pieteikums']);
  }
}
